import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { RadarChart as RechartsRadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer } from 'recharts';

const BENCHMARK = { Technology: 75, Market: 80, Innovation: 70, Financial: 85, Delivery: 78, Strategic: 72 };

const StrategicFitRadar = ({ partnerData, enablementScores }) => {
  // Build radar data: prefer enablementScores if provided, else derive from partnerData roadmapFit
  const radarData = useMemo(() => {
    if (enablementScores && typeof enablementScores === 'object') {
      return [
        { dimension: 'Technology', partner: enablementScores?.technology || enablementScores?.technical || 75, benchmark: BENCHMARK?.Technology, fullMark: 100 },
        { dimension: 'Market', partner: enablementScores?.market || enablementScores?.sales || 78, benchmark: BENCHMARK?.Market, fullMark: 100 },
        { dimension: 'Innovation', partner: enablementScores?.innovation || 82, benchmark: BENCHMARK?.Innovation, fullMark: 100 },
        { dimension: 'Financial', partner: enablementScores?.financial || 70, benchmark: BENCHMARK?.Financial, fullMark: 100 },
        { dimension: 'Delivery', partner: enablementScores?.delivery || enablementScores?.operations || 88, benchmark: BENCHMARK?.Delivery, fullMark: 100 },
        { dimension: 'Strategic', partner: enablementScores?.strategic || enablementScores?.overall || 82, benchmark: BENCHMARK?.Strategic, fullMark: 100 },
      ];
    }

    // Derive from roadmapFit sliders
    const rf = partnerData?.roadmapFit || {};
    return [
      { dimension: 'Technology', partner: rf?.technology || 75, benchmark: BENCHMARK?.Technology, fullMark: 100 },
      { dimension: 'Market', partner: rf?.market || 78, benchmark: BENCHMARK?.Market, fullMark: 100 },
      { dimension: 'Innovation', partner: rf?.innovation || 82, benchmark: BENCHMARK?.Innovation, fullMark: 100 },
      { dimension: 'Financial', partner: Math.round(((rf?.technology || 75) + (rf?.market || 78)) / 2 * 0.9), benchmark: BENCHMARK?.Financial, fullMark: 100 },
      { dimension: 'Delivery', partner: Math.round(((rf?.sustainability || 70) + (rf?.technology || 75)) / 2 * 1.1), benchmark: BENCHMARK?.Delivery, fullMark: 100 },
      { dimension: 'Strategic', partner: rf?.sustainability || 70, benchmark: BENCHMARK?.Strategic, fullMark: 100 },
    ];
  }, [partnerData, enablementScores]);

  const overallScore = Math.round(radarData?.reduce((sum, item) => sum + item?.partner, 0) / radarData?.length);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-primary';
    if (score >= 40) return 'text-warning';
    return 'text-error';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent Fit';
    if (score >= 60) return 'Good Fit';
    if (score >= 40) return 'Moderate Fit';
    return 'Poor Fit';
  };

  // Dynamic recommendations based on scores
  const recommendations = useMemo(() => {
    const sorted = [...radarData]?.sort((a, b) => (b?.partner - b?.benchmark) - (a?.partner - a?.benchmark));
    const top = sorted?.[0];
    const bottom = sorted?.[sorted?.length - 1];
    return [
      top ? { icon: 'CheckCircle2', color: 'text-success', text: `${top?.dimension} capability exceeds benchmark by ${top?.partner - top?.benchmark} points — leverage for competitive advantage` } : null,
      bottom && bottom?.partner < bottom?.benchmark ? { icon: 'AlertCircle', color: 'text-warning', text: `${bottom?.dimension} alignment below benchmark — consider targeted investment` } : null,
      { icon: 'TrendingUp', color: 'text-primary', text: `Overall strategic fit score: ${overallScore} — ${getScoreLabel(overallScore)}` },
    ]?.filter(Boolean);
  }, [radarData, overallScore]);

  const isLive = !!enablementScores;

  return (
    <div>
      {isLive && (
        <div className="flex items-center gap-8 mb-16 px-12 py-8 bg-success/5 border border-success/20 rounded-lg">
          <Icon name="Zap" size={14} className="text-success" />
          <span className="text-xs text-success font-medium">Live data from enablement scores</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-24 mb-24">
        <div className="lg:col-span-2">
          <div className="h-[350px] w-full" aria-label="Strategic Fit Radar Chart">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsRadarChart data={radarData}>
                <PolarGrid stroke="var(--color-border)" />
                <PolarAngleAxis dataKey="dimension" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="var(--color-muted-foreground)" style={{ fontSize: '10px' }} />
                <Radar name="Partner Score" dataKey="partner" stroke="var(--color-primary)" fill="var(--color-primary)" fillOpacity={0.3} />
                <Radar name="Industry Benchmark" dataKey="benchmark" stroke="var(--color-muted-foreground)" fill="var(--color-muted-foreground)" fillOpacity={0.1} />
                <Legend />
              </RechartsRadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-16">
          <div className="p-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg border border-primary/20">
            <div className="text-sm text-muted-foreground caption mb-8">Overall Strategic Fit</div>
            <div className={`text-4xl font-bold mb-8 data-text ${getScoreColor(overallScore)}`}>{overallScore}</div>
            <div className="text-sm font-medium text-foreground">{getScoreLabel(overallScore)}</div>
          </div>

          <div className="space-y-8">
            <div className="text-sm font-medium text-foreground mb-12">Dimension Scores</div>
            {radarData?.map((item, index) => (
              <div key={index} className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">{item?.dimension}</span>
                  <span className={`text-sm font-semibold data-text ${getScoreColor(item?.partner)}`}>{item?.partner}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary transition-smooth" style={{ width: `${item?.partner}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-24">
        {radarData?.map((item, index) => {
          const variance = item?.partner - item?.benchmark;
          return (
            <div key={index} className="p-16 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-12">
                <h4 className="text-sm font-medium text-foreground">{item?.dimension} Alignment</h4>
                <Icon name={variance >= 0 ? 'TrendingUp' : 'TrendingDown'} size={16} className={variance >= 0 ? 'text-success' : 'text-error'} />
              </div>
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground caption">Partner Score</span>
                  <span className={`text-sm font-semibold data-text ${getScoreColor(item?.partner)}`}>{item?.partner}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground caption">Benchmark</span>
                  <span className="text-sm font-medium text-muted-foreground data-text">{item?.benchmark}</span>
                </div>
                <div className="flex items-center justify-between pt-8 border-t border-border">
                  <span className="text-xs text-muted-foreground caption">Variance</span>
                  <span className={`text-sm font-semibold data-text ${variance >= 0 ? 'text-success' : 'text-error'}`}>
                    {variance >= 0 ? '+' : ''}{variance}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-16 bg-primary/5 rounded-md border border-primary/20">
        <div className="flex items-start gap-12">
          <Icon name="Lightbulb" size={18} className="text-primary flex-shrink-0 mt-2" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-8">Strategic Recommendations</h4>
            <ul className="space-y-6">
              {recommendations?.map((rec, i) => (
                <li key={i} className="flex items-start gap-8 text-sm text-muted-foreground">
                  <Icon name={rec?.icon} size={14} className={`${rec?.color} flex-shrink-0 mt-2`} />
                  <span>{rec?.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategicFitRadar;